import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
    CardAccountLoad,
    DeepPartial,
    IAccountBalanceResponse,
    IAprDetails,
    ICardAuth,
    ICardProduct,
    ICardProductsQueryParams,
    ICardProductTransaction,
    ICardSkin,
    ICharge,
    IChargeQueryParams,
    IFee,
    IFxRateGroupListResponse,
    IGetCardProductMetadata,
    ILimit,
    ILimitList,
    ILimitQueryParams,
    ILinkUnlinkChargeRequest,
    IListResponse,
    IMutateCharge,
    IMutateLimit,
    INotifications,
    INotificationsQueryParams,
    LimitType,
    LinkOrUnlink,
} from '@lib/interfaces';
import { IDelinqiencies, IDelinqienciesQueryParams, IMutateDelinqiencies } from '@lib/interfaces/delinqiencies.interface';
import { IWebHooks } from '@lib/interfaces/web-hook.interface';
import { map, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ProductManagementService {
    public environment_url: string;
    public httpClient: HttpClient;

    public constructor(private readonly http: HttpClient, private httpHandler: HttpBackend) {
        this.environment_url = environment.api_url;
        this.httpClient = new HttpClient(httpHandler);
    }

    /**
     * CARD PRODUCT API METHODS
     */
    public fetchCardProducts(cardProductsQueryParams: DeepPartial<ICardProductsQueryParams>): Observable<IListResponse<ICardProduct>> {
        const url: URL = new URL(`${this.environment_url}nfront/v1/proxy/cardproducts`);

        const queryParams: URLSearchParams = new URLSearchParams(
            Object.entries(cardProductsQueryParams).map(([key, value]: [string, string | number]): [string, string] => {
                return [key, value.toString()];
            }),
        );

        url.search = queryParams.toString();

        return this.http.get<IListResponse<ICardProduct>>(url.toString());
    }

    public updateCardProductDetails(id: string, payload: DeepPartial<ICardProduct>): Observable<ICardProduct> {
        return this.http.put<ICardProduct>(`${this.environment_url}nfront/v1/proxy/cardproducts/${id}`, payload);
    }

    public updateAuthControlDetails(cardId: string, payload: DeepPartial<ICardAuth>): Observable<ICardAuth> {
        return this.http.put<ICardAuth>(`${this.environment_url}nfront/v1/proxy/cards/${cardId}/authsettings`, payload);
    }

    /**
     *  CARD PRODUCT LIMITS API METHODS
     */
    public fetchCardProductLimitsDetailsById(id: string, limitType: LimitType): Observable<ILimit> {
        return this.http.get<ILimit>(`${this.environment_url}nfront/v1/proxy/${limitType}/${id}`);
    }

    public updateCardProductLimitsDetailsById(id: string, limitType: LimitType, payload: any): Observable<ILimit> {
        return this.http.put<ILimit>(`${this.environment_url}nfront/v1/proxy/${limitType}/${id}`, payload);
    }

    public fetchProductLimits(cardProductId: string, limitType: LimitType): Observable<Array<string>> {
        return this.http.get<Array<string>>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/${limitType}`);
    }

    public fetchLimits(limitType: LimitType): Observable<Array<ILimit>> {
        return this.http.get<IListResponse<ILimit>>(`${this.environment_url}nfront/v1/proxy/${limitType}?limit=100`).pipe(
            map(({ data }: IListResponse<ILimit>) => {
                return data;
            }),
        );
    }

    public fetchLimitsList(limitType: LimitType, after: string): Observable<ILimitList> {
        return this.http.get<ILimitList>(`${this.environment_url}nfront/v1/proxy/${limitType}?limit=100&after=${after}`).pipe(
            map((limitList: ILimitList) => {
                return limitList;
            }),
        );
    }

    public unlinkLimitFromCardProduct(cardProductId: string, limitIds: Array<string>, limitType: LimitType): Observable<Array<string>> {
        return this.http.post<Array<string>>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/${limitType}:unlink`, { ids: limitIds }).pipe(
            map((remainingLimits: Array<string>) => {
                return remainingLimits;
            }),
        );
    }

    public linkLimitToCardProduct(cardProductId: string, limitIds: Array<string>, limitType: LimitType): Observable<Array<string>> {
        return this.http.post<Array<string>>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/${limitType}:link`, { ids: limitIds }).pipe(
            map((linkedLimits: Array<string>) => {
                return linkedLimits;
            }),
        );
    }

    public getConditionsAdv(): Observable<any> {
        return this.http.get<ILimit>(`${this.environment_url}nfront/v1/proxy/velocitylimits/conditiontypes?limit=50`);
    }

    public getConditionsAdvFee(): Observable<any> {
        return this.http.get<any>(`${this.environment_url}nfront/v1/proxy/fees/conditiontypes?limit=50`);
    }

    /**
     * PRODUCT SETTING API METHODS
     */
    public fetchProductSettingLimits(limitQueryParams: DeepPartial<ILimitQueryParams>, limitType: LimitType): Observable<IListResponse<ILimit>> {
        const url: URL = new URL(`${this.environment_url}nfront/v1/proxy/${limitType}`);
        const queryParams: URLSearchParams = new URLSearchParams(
            Object.entries(limitQueryParams).map(([key, value]: [string, string | number]): [string, string] => {
                return [key, value.toString()];
            }),
        );

        url.search = queryParams.toString();

        return this.http.get<IListResponse<ILimit>>(url.toString());
    }

    public createProductSettingLimit(limitPayload: DeepPartial<IMutateLimit>, limitType: LimitType): Observable<ILimit> {
        return this.http.post<ILimit>(`${this.environment_url}nfront/v1/proxy/${limitType}`, limitPayload);
    }

    public updateProductSettingLimit(limitId: string, limitPayload: DeepPartial<IMutateLimit>, limitType: LimitType): Observable<ILimit> {
        return this.http.put<ILimit>(`${this.environment_url}nfront/v1/proxy/${limitType}/${limitId}`, limitPayload);
    }

    public fetchProductSettingCharges(chargeQueryParams: DeepPartial<IChargeQueryParams>): Observable<IListResponse<ICharge>> {
        const url: URL = new URL(`${this.environment_url}nfront/v1/proxy/charges`);

        const queryParams: URLSearchParams = new URLSearchParams(
            Object.entries(chargeQueryParams).map(([key, value]: [string, string | number]): [string, string] => {
                return [key, value.toString()];
            }),
        );

        url.search = queryParams.toString();

        return this.http.get<IListResponse<ICharge>>(url.toString());
    }

    public createProductDelinquencyActions(delinquencyPayload: DeepPartial<IMutateDelinqiencies>): Observable<IDelinqiencies> {
        return this.http.post<IDelinqiencies>(`${this.environment_url}nfront/v1/proxy/cardproducts/delinqiencies`, { delinquency_actions: [delinquencyPayload] });
    }

    public updateProductDelinquencyActions(delinquencyId: string, delinquencyPayload: DeepPartial<IMutateDelinqiencies>): Observable<IDelinqiencies> {
        const { id, ...rest } = delinquencyPayload
        return this.http.put<IDelinqiencies>(`${this.environment_url}nfront/v1/proxy/cardproducts/delinqiencies/${delinquencyId}`, rest);
    }

    public fetchDelinquencyActions(chargeQueryParams: DeepPartial<IDelinqienciesQueryParams>): Observable<IListResponse<IDelinqiencies>> {
        const url: URL = new URL(`${this.environment_url}nfront/v1/proxy/cardproducts/delinqiencies`);

        const queryParams: URLSearchParams = new URLSearchParams(
            Object.entries(chargeQueryParams).map(([key, value]: [string, string | number]): [string, string] => {
                return [key, value.toString()];
            }),
        );

        url.search = queryParams.toString();

        return this.http.get<IListResponse<IDelinqiencies>>(url.toString());
    }

    public createProductSettingCharge(chargePayload: DeepPartial<IMutateCharge>): Observable<ICharge> {
        return this.http.post<ICharge>(`${this.environment_url}nfront/v1/proxy/charges`, chargePayload);
    }

    public updateProductSettingCharge(chargeId: string, chargePayload: DeepPartial<IMutateCharge>): Observable<ICharge> {
        return this.http.put<ICharge>(`${this.environment_url}nfront/v1/proxy/charges/${chargeId}`, chargePayload);
    }

    public fetchNotifications(notificationsQueryParams: DeepPartial<INotificationsQueryParams>): Observable<IListResponse<INotifications>> {
        const url: URL = new URL(`${this.environment_url}nfront/v3/proxy/notificationtemplates`);

        if (notificationsQueryParams === null || notificationsQueryParams === undefined) {
            throw new Error('Something went wrong');
        }

        const queryParams: URLSearchParams = new URLSearchParams(
            Object.entries(notificationsQueryParams).map(([key, value]: [string, string | number]): [string, string] => {
                return [key, value ? value.toString() : ''];
            }),
        );

        url.search = queryParams.toString();
        return this.http.get<IListResponse<INotifications>>(url.toString());
    }

    public createProductSettingNotifications(payload: DeepPartial<INotifications>): Observable<INotifications> {
        return this.http.post<INotifications>(`${this.environment_url}nfront/v3/proxy/notificationtemplates`, payload);
    }

    public deleteProductSettingNotifications(payload: DeepPartial<INotifications>): Observable<INotifications> {
        return this.http.delete<INotifications>(`${this.environment_url}nfront/v3/proxy/notificationtemplates/${payload.id}`);
    }

    public updateProductSettingNotifications(notificationId: string, notificationPayload: DeepPartial<INotifications>): Observable<INotifications> {
        return this.http.put<INotifications>(`${this.environment_url}nfront/v3/proxy/notificationtemplates/${notificationId}`, notificationPayload);
    }

    /**
     * CARD PRODUCT CHARGE API METHODS
     */
    public fetchAllCardProductCharges(after = ''): Observable<IListResponse<ICharge>> {
        return this.http.get<IListResponse<ICharge>>(`${this.environment_url}nfront/v1/proxy/charges?limit=100&after=${after}`);
    }

    public fetchCardProductCharges(cardProductId: string): Observable<Array<string>> {
        return this.http.get<Array<string>>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/charges`);
    }

    public linkOrUnlinkChargeToCardProduct(cardProductId: string, linkOrUnlink: LinkOrUnlink, linkChargeRequest: ILinkUnlinkChargeRequest): Observable<Array<string>> {
        return this.http.post<Array<string>>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/charges:${linkOrUnlink}`, linkChargeRequest);
    }

    /**
     * CARD PRODUCT Deliquencies API METHODS
     */
    public fetchAllCardProductDeliquencies(after = ''): Observable<IListResponse<any>> {
        return this.http.get<IListResponse<any>>(`${this.environment_url}nfront/v1/proxy/cardproducts/delinqiencies?limit=100&after=${after}`);
    }

    public fetchCardProductDeliquencies(cardProductId: string): Observable<Array<string>> {
        return this.http.get<Array<string>>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/delinquencies`);
    }

    public linkOrUnlinkDeliquenciesToCardProduct(cardProductId: string, linkOrUnlink: LinkOrUnlink, linkDelinqienciesRequest: { delinquency_id: string }): Observable<Array<string>> {
        return this.http.post<Array<string>>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/delinqiencies:${linkOrUnlink}`, linkDelinqienciesRequest);
    }

    /**
     * CARD PRODUCT SKIN DESIGN METHODS
     */
    public fetchCardDesignsMetaDataById(cardProductId: string): Observable<any> {
        return this.http.get<any>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/card_designs/metadata`);
    }

    public uploadCardSkin(productId: string, payload: any, designId?: string): Observable<any> {
        if (designId) {
            return this.http.put<any>(`${this.environment_url}nfront/v1/proxy/cardproducts/${productId}/card_designs/${designId}`, payload);
        } else {
            return this.http.post<any>(`${this.environment_url}nfront/v1/proxy/cardproducts/${productId}/card_designs`, payload);
        }
    }

    public getCardSkin(productId: string, designId: string): Observable<ICardSkin> {
        return this.http.get<ICardSkin>(`${this.environment_url}nfront/v1/proxy/cardproducts/${productId}/card_designs/${designId}`);
    }

    public getCardSkinMetadata(productId: string): Observable<IGetCardProductMetadata> {
        return this.http.get<IGetCardProductMetadata>(`${this.environment_url}nfront/v1/proxy/cardproducts/${productId}/card_designs/metadata`);
    }

    public createProduct(product: DeepPartial<ICardProduct>): Observable<ICardProduct> {
        return this.http.post<ICardProduct>(`${this.environment_url}nfront/v1/proxy/cardproducts`, product);
    }

    public getFXRateGroups(): Observable<IFxRateGroupListResponse> {
        return this.httpClient.get<IFxRateGroupListResponse>(`${this.environment_url}nfront/v1/proxy/fxrates/groups`);
    }

    // Web Hooks
    public fetchWebHooks(): Observable<Array<IWebHooks>> {
        return this.http.get<any>(`${this.environment_url}nfront/v1/proxy/webhooks`);
    }

    public createWebHook(payload: DeepPartial<IWebHooks>): Observable<IWebHooks> {
        return this.http.post<IWebHooks>(`${this.environment_url}nfront/v1/proxy/webhooks`, payload);
    }

    public updateWebHook(webHookId: string, WebHookPayload: DeepPartial<IWebHooks>): Observable<IWebHooks> {
        return this.http.put<IWebHooks>(`${this.environment_url}nfront/v1/proxy/webhooks/${webHookId}`, WebHookPayload);
    }

    public pingWebHook(webHookId: string, payload: string): Observable<IWebHooks> {
        return this.http.post<IWebHooks>(`${this.environment_url}nfront/v1/proxy/webhooks/${webHookId}:ping`, payload);
    }

    public createAprDetails(cardproductId: string, payload: IAprDetails[]): Observable<IAprDetails> {
        return this.http.post<IAprDetails>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardproductId}/:apr_profile`, payload);
    }

    public linkFees(feeType: string, cardProductId: string, payload: any) {
        return this.http.post(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/${feeType}:link`, payload);
    }

    public fetchProductFees(feeType: string, cardProductId: string): Observable<Array<string>> {
        return this.http.get<Array<string>>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardProductId}/${feeType}`);
    }

    public fetchFees(feeType: string, after: string): Observable<IListResponse<IFee>> {
        return this.http.get<IListResponse<IFee>>(`${this.environment_url}nfront/v1/proxy/${feeType}?limit=100&after=${after}`).pipe(
            map((feeList: IListResponse<IFee>) => {
                return feeList;
            }),
        );
    }

    public fetchFeeById(feeType: string, feeId: string) {
        return this.http.get<IFee>(`${this.environment_url}nfront/v1/proxy/${feeType}/${feeId}`);
    }

    public fetchChargeById(chargeId: any) {
        return this.http.get<any>(`${this.environment_url}nfront/v1/proxy/charges/${chargeId}`);
    }

    public fetchDelinquenciesById(delinqiencyId: any) {
        return this.http.get<any>(`${this.environment_url}nfront/v1/proxy/cardproducts/delinqiencies/${delinqiencyId}`);
    }

    public fetchCardTypeAccountBalances(cardId: string, accountId: string, currencies: string[]): Observable<IAccountBalanceResponse> {
        const currenciesParam = currencies.join('%2C');
        return this.http.get<IAccountBalanceResponse>(
            `${this.environment_url}nfront/v1/proxy/cardproducts/${cardId}/accountbalance?currencies=${currenciesParam}&account_id=${accountId}`,
        );
    }

    public fetchCardTypeLoadUnLoadAccount(payload: CardAccountLoad, accountId: string, cardId: string, fundType: string) {
        return this.http.post<any>(`${this.environment_url}nfront/v1/proxy/accounts/cardproducts/${cardId}/globalaccount/${accountId}:${fundType}`, payload, {
            headers: new HttpHeaders({
                'x-nymos-idempotency-key': crypto.randomUUID(),
            }),
        });
    }

    public fetchCardTransactions(cardId: string, limit: number = 10, page: number = 1): Observable<IListResponse<ICardProductTransaction>> {
        return this.http.get<IListResponse<ICardProductTransaction>>(`${this.environment_url}nfront/v1/proxy/cardproducts/${cardId}/transactiondetails`, {
            params: { limit, page },
        });
    }
}
